import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'src/services/api';
import { UseQueryOverrideOptions } from '../common';
import { NonOptional } from '@insight-health-ai/roseate/utils';

export type SearchedPatient = {
  next_appointment_date: string | null;
  past_appointment_date: string | null;
  patient_Name: string;
  patient_birth_date: string;
  patient_id: number;
  ehr_id: string;
};

export type GetPatientListResponse = {
  patients: SearchedPatient[];
  total: number;
  pages: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
};

export type UseGetPatientListParams = {
  search?: string;
  page?: number;
  pageSize?: number;
};

const defaultPageSize = 50;

const filterToParams = ({
  search,
  page = 1,
  pageSize,
}: UseGetPatientListParams) => {
  return {
    search_term: search,
    page,
    per_page: pageSize,
  };
};

export const useGetPatientList = (
  params: UseGetPatientListParams,
  options: UseQueryOverrideOptions = {}
) => {
  return useQuery({
    ...options,
    queryKey: ['patientList', params],
    queryFn: () =>
      api
        .get<GetPatientListResponse>('/v2/patients/search', {
          params: filterToParams({
            page: 1,
            pageSize: defaultPageSize,
            ...params,
          }),
        })
        .then((response) => response.data),
  });
};

export const useGetPatientListInfinite = (
  filters: UseGetPatientListParams,
  options: UseQueryOverrideOptions = {}
) => {
  return useInfiniteQuery({
    ...options,
    queryKey: ['patientList', 'infinite', filters],
    queryFn: ({ pageParam }) =>
      api
        .get<GetPatientListResponse>('/v2/patients/search', {
          params: filterToParams(pageParam),
        })
        .then((response) => response.data),
    initialPageParam: {
      page: 1,
      pageSize: defaultPageSize,
      ...filters,
    } as NonOptional<UseGetPatientListParams, 'page' | 'pageSize'>,
    getNextPageParam: (lastPage) => {
      return lastPage.page * lastPage.per_page < lastPage.total
        ? { pageSize: defaultPageSize, ...filters, page: lastPage.page + 1 }
        : undefined;
    },
  });
};
export const usePatientListClient = () => {
  const queryClient = useQueryClient();

  const deletePatient = ({ patientId }: { patientId: number }) => {
    queryClient.setQueriesData<ReturnType<typeof useGetPatientList>['data']>(
      { queryKey: ['patientList'] },
      (oldData) => {
        if (!oldData?.patients?.length) return oldData;
        return {
          ...oldData,
          patients: oldData.patients.filter(
            (patient) => patient.patient_id !== patientId
          ),
        };
      }
    );
    queryClient.setQueriesData<
      ReturnType<typeof useGetPatientListInfinite>['data']
    >({ queryKey: ['patientList', 'infinite'] }, (oldData) => {
      if (!oldData?.pages?.length) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          patients: page.patients.filter(
            (patient) => patient.patient_id !== patientId
          ),
        })),
      };
    });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['patientList'] });
  };

  return { deletePatient, invalidate };
};
